import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { ConvexReactClient } from "convex/react"
import {
  ConvexBetterAuthProvider,
  type AuthClient as ConvexAuthClient,
} from "@convex-dev/better-auth/react"
import { authClient } from "@/lib/auth-client"
import { errorMessage } from "@/lib/errors"
import { initPostHog } from "@/lib/posthog"
import { routeTree } from "./routeTree.gen"
import { TooltipProvider } from "@/components/ui/tooltip"
import "./index.css"

initPostHog()

// Recovery from a chunk that fails to import (stale tab after a Pages deploy,
// or a dropped request) is the router's job, not ours: lazyRouteComponent
// reloads once per missing module, keyed by the error message, and only when
// the route is actually being rendered. A `vite:preloadError` listener cannot
// see that distinction -- it also fires for the speculative imports that
// `defaultPreload: "intent"` starts on hover, so reloading from here threw
// away unsaved form state for a prefetch nobody was waiting on, and spent the
// tab's one recovery before a real navigation could use it.

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string)

// @convex-dev/better-auth 0.12.5 still describes the 1.6.15 client type. The
// 1.6.22 security patch preserves this runtime interface but changes inferred
// generic details, so narrow the assertion at the adapter boundary only.
const convexAuthClient = authClient as unknown as ConvexAuthClient

const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: "intent",
  defaultNotFoundComponent: () => (
    <div className="mkt-page home-wf min-h-svh">
      <div className="mkt-page-shell mkt-page-shell--narrow py-16 text-center">
        <h1 className="mkt-page-title">Page not found</h1>
        <p className="mkt-page-sub mx-auto max-w-md">
          The page you're looking for doesn't exist or has moved.
        </p>
        <a href="/" className="home-wf-btn home-wf-btn--lime mt-8 inline-flex">
          Back to home
        </a>
      </div>
    </div>
  ),
  defaultErrorComponent: ({ error }) => (
    <div className="mkt-page home-wf min-h-svh">
      <div className="mkt-page-shell mkt-page-shell--narrow py-16 text-center">
        <h1 className="mkt-page-title">Something went wrong</h1>
        <p className="mkt-page-sub mx-auto max-w-lg">
          {errorMessage(error, "An unexpected error occurred.")}
        </p>
        <a href="/" className="home-wf-btn home-wf-btn--lime mt-8 inline-flex">
          Back to home
        </a>
      </div>
    </div>
  ),
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const app = (
  <ConvexBetterAuthProvider client={convex} authClient={convexAuthClient}>
    <TooltipProvider>
      <RouterProvider router={router} />
    </TooltipProvider>
  </ConvexBetterAuthProvider>
)

createRoot(document.getElementById("root")!).render(
  <StrictMode>{app}</StrictMode>,
)
